// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One machine's file (design doc §10.1, §9.5, §8.6, US-C-01, US-B-01).
 *
 * The version history is a timeline down the right-hand side rather than a tab, because it
 * is context for everything else on the page: whichever view is open — the current
 * assembly, a comparison, the maintenance log — the question "how did it get here" is the
 * one being asked alongside it. As a tab it was a place you had to leave to consult.
 *
 * 变更 sits next to the archive number because it is the one thing you *do* to a machine
 * from this page; everything else here is reading. Scrapping is not offered here at all —
 * it belongs to the list, where someone is looking at a fleet and deciding what is still in
 * service.
 *
 * The header carries the version number on purpose. Every write against this archive sends
 * it as `base_version`, and when the server answers 409 the dialog names two numbers — the
 * one the operator started from and the one that is current. Those numbers mean nothing
 * unless the page they came from showed them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useOutletContext, useParams } from "react-router-dom";
import { ApiError, newIdempotencyKey } from "@/api/client";
import { api } from "@/api/endpoints";
import type { AffectedArchive, ArchiveDetail, ChainLink } from "@/api/types";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { ConflictDialog } from "@/components/ConflictDialog";
import { CopyButton } from "@/components/CopyButton";
import { DeviceNoEditor } from "@/components/DeviceNoEditor";
import { ErrorBanner } from "@/components/ErrorBanner";
import { MaintenanceDialog, type MaintenanceSubmission } from "@/components/MaintenanceDialog";
import { SnCard } from "@/components/SnCard";
import { StatusBadge } from "@/components/StatusBadge";
import { VersionTimeline } from "@/components/VersionTimeline";
import { usePermissions } from "@/hooks/usePermissions";
import { archiveLink } from "@/links";

export interface ArchiveContext {
  archive: ArchiveDetail;
}

export function useArchive(): ArchiveContext {
  return useOutletContext<ArchiveContext>();
}

export function ArchiveDetailPage() {
  const { t, i18n } = useTranslation();
  const { canRecord } = usePermissions();
  const { archiveId = "" } = useParams();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState(false);

  const archive = useQuery({
    queryKey: ["archive", archiveId],
    // Expanded: a module slot carries the module it holds, at the version pinned, with its
    // contents under it (§9.4).
    queryFn: async () => {
      try {
        return await api.archives.get(archiveId, true);
      } catch (error) {
        // Too big to expand in one go (§15): the archive alone, and each module's contents
        // load when it is opened.
        if (error instanceof ApiError && error.code === "ARCHIVE_EXPAND_TOO_LARGE") {
          return api.archives.get(archiveId);
        }
        throw error;
      }
    },
  });
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });

  const submit = useMutation({
    mutationFn: (submission: MaintenanceSubmission) =>
      api.maintenance.submit(
        archiveId,
        {
          base_version: archive.data?.current_version ?? 0,
          // Left blank, the time of submission — not of opening the form, and by the
          // server's clock, not this computer's: sent as no time at all. A computer whose
          // clock runs fast would otherwise be refused for a time nobody typed.
          occurred_at: submission.occurredAt ? new Date(submission.occurredAt).toISOString() : null,
          change_type: submission.changeType,
          reason: submission.reason,
          jira_url: submission.jiraUrl,
          lines: submission.lines,
        },
        // One key per attempt, so a timeout followed by a retry records one event (§7.7).
        newIdempotencyKey(),
      ),
    onSuccess: async (result) => {
      setChanging(false);
      // More than this archive moved when the change reached into a module (§9.5), or was
      // made on a fitted module's page (§9.6): say which, with their new versions.
      setAffected((result.affected ?? []).length > 1 ? (result.affected ?? []) : []);
      await refreshEverything();
    },
  });
  const [affected, setAffected] = useState<AffectedArchive[]>([]);

  async function refreshEverything() {
    // Everything this archive is shown through: the header, the timeline beside it and the
    // event list under it all describe the state that just moved.
    // Every archive, not just this one: a change inside a module moves the module and
    // everything it is fitted in.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["archive"] }),
      queryClient.invalidateQueries({ queryKey: ["archive-versions"] }),
      queryClient.invalidateQueries({ queryKey: ["maintenance"] }),
      queryClient.invalidateQueries({ queryKey: ["archive-installations"] }),
    ]);
  }

  if (archive.isLoading) return <p className="muted">{t("common.loading")}</p>;
  if (archive.error) return <ErrorBanner error={archive.error} />;
  if (!archive.data) return null;

  const detail = archive.data;
  const isModule = detail.kind === "MODULE";
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");
  const offBomCount = detail.items.filter((item) => item.off_bom).length;
  const conflict =
    submit.error instanceof ApiError && submit.error.isVersionConflict ? submit.error : null;

  return (
    <section>
      <h1 className="record-header">
        <span className="archive-no">{detail.archive_no}</span>
        {isModule ? (
          <>
            <span className="module-badge">{typeLabel(detail.module_type ?? "")}</span>
            <span className="mono muted">{detail.module_sn}</span>
          </>
        ) : (
          <DeviceNoEditor archiveId={detail.id} deviceNo={detail.device_no} />
        )}
        <StatusBadge status={detail.status} />
        <span className="badge badge-version">v{detail.current_version}</span>
        <CopyButton
          value={archiveLink(detail.id)}
          label={t("archive.copyLink")}
          text={t("common.copyLink")}
        />
        {detail.status === "ACTIVE" && canRecord && (
          <button type="button" className="button-primary" onClick={() => setChanging(true)}>
            {t("maintenance.start")}
          </button>
        )}
      </h1>
      {/* Directly under the number: the BOM version is the baseline everything on this
          page is read against, and "which BOM" was previously answerable only by opening
          the filing screen. The off-BOM count sits with it because it is a statement about
          that baseline — how far this machine has moved from it. */}
      {/* Where a module is now, step by step up to the robot — or that it is in stock (§9.4). */}
      {isModule && (
        <p className="record-meta module-chain">
          <span>{t("moduleArchive.location")}: </span>
          <ModuleChain chain={detail.installed_on ?? []} typeLabel={typeLabel} />
        </p>
      )}
      <p className="record-meta">
        {detail.source_bom_id ? (
          <>
            <span>{t("archive.bomVersion")}: </span>
            <Link to={`/boms/${detail.source_bom_id}`}>
              {detail.source_bom_version ?? detail.source_bom_id}
            </Link>
            {detail.source_bom_name && <span className="muted"> · {detail.source_bom_name}</span>}
          </>
        ) : (
          <span>{t("archive.noBom")}</span>
        )}
        {offBomCount > 0 && (
          <span className="badge badge-off-bom">
            {t("archive.offBomCount", { n: offBomCount })}
          </span>
        )}
        <span className="muted">
          {" · "}
          {t("archive.createdAt")}: {new Date(detail.created_at).toLocaleString()}
        </span>
      </p>

      {affected.length > 0 && (
        <div className="banner banner-info" role="status">
          {t("maintenance.affected", { list: "" })}
          {affected.map((item, index) => (
            <span key={item.archive_id}>
              {index > 0 && "，"}
              <Link to={`/archives/${item.archive_id}`}>{item.archive_no}</Link> v
              {item.result_version}
            </span>
          ))}
        </div>
      )}

      <div className="record-layout">
        <div className="record-main">
          <nav className="tabs">
            <NavLink to="current">{t("archive.current")}</NavLink>
            <NavLink to="compare">{t("archive.compare")}</NavLink>
            <NavLink to="maintenance">{t("archive.maintenance")}</NavLink>
            {isModule && <NavLink to="installations">{t("moduleArchive.installations")}</NavLink>}
          </nav>
          <Outlet context={{ archive: detail } satisfies ArchiveContext} />
        </div>

        <div className="record-aside">
          {/* Beside everything else on the page: what is printed on the machine's label,
              and a scan of it finds this page again. */}
          {detail.robot_sn && (
            <SnCard
              sn={detail.robot_sn}
              symbology="qr"
              label={t("robotSn.label")}
              printLines={[detail.archive_no, detail.device_no ?? ""].filter(Boolean)}
            />
          )}
          {detail.module_sn && (
            <SnCard
              sn={detail.module_sn}
              symbology="code128"
              label={t("moduleFiling.snLabel")}
              printLines={[detail.archive_no, typeLabel(detail.module_type ?? "")]}
            />
          )}
          <VersionTimeline archiveId={archiveId} currentVersion={detail.current_version} />
        </div>
      </div>

      {changing && (
        <MaintenanceDialog
          archive={detail}
          pending={submit.isPending}
          error={conflict ? null : <ErrorBanner error={submit.error} />}
          onSubmit={(submission) => submit.mutate(submission)}
          onClose={() => {
            submit.reset();
            setChanging(false);
          }}
        />
      )}

      {conflict && (
        <ConflictDialog
          baseVersion={detail.current_version}
          latestVersion={conflict.latestVersion ?? detail.current_version}
          archiveHref={`/archives/${archiveId}/current`}
          onRefresh={() => {
            submit.reset();
            setChanging(false);
            void refreshEverything();
          }}
          onClose={() => submit.reset()}
        />
      )}
    </section>
  );
}

/** 「舵轮 #2 · RBT-A-000038 底盘 · RBT-000123 整机」, each step a link; 「在库」 when empty. */
export function ModuleChain({
  chain,
  typeLabel,
}: {
  chain: ChainLink[];
  typeLabel: (code: string) => string;
}) {
  const { t } = useTranslation();
  if (chain.length === 0) return <span className="badge">{t("moduleArchive.inStock")}</span>;
  return (
    <span className="chain">
      {chain.map((link, index) => (
        <span key={link.archive_id}>
          {index > 0 && " · "}
          {index === 0 && <span>{link.position_name} · </span>}
          <Link to={`/archives/${link.archive_id}`}>{link.archive_no}</Link>{" "}
          {link.kind === "MODULE" ? (
            <span className="module-badge">{typeLabel(link.module_type ?? "")}</span>
          ) : (
            <span className="muted">{t("archive.kindRobot")}</span>
          )}
        </span>
      ))}
    </span>
  );
}
